// Global Instructions Rule Applied!
//
// Account form — Deals & Rounds tab (signed-off Mockup 8). Two tables because
// neither entity is a subset of the other: rounds carry the company's full
// financing history (with or without our participation); deals carry our
// actions (with or without a round). "Passed" shows only when we had deals on
// the round and every one was passed; a round we were never offered shows "—".
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Popconfirm, Spin, Table, Tag, Tooltip } from 'antd'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { addRound, removeRound } from '../../../state_manager/state_update_actions/rounds/index.ts'
import { deleteRound } from '../../rounds/services/roundsApi.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useQuickAdd } from '../../../core/contexts/QuickAddContext.jsx'
import { daysSince } from '../../../core/utils/pipelineAge.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import AddRoundModal from '../../rounds/components/AddRoundModal.tsx'
import AddSecurityModal from '../../asset_transactions/components/AddSecurityModal.tsx'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'
import {
  AccountDealsRoundsData,
  fetchAccountDealsRounds
} from '../services/dealExecutionService.ts'

interface AccountDealsRoundsTabProps {
  accountId: string | null
  accountName?: string | null
  /** Hands `refetch` to the company form so its Refresh button reloads this
   *  tab (same contract as CapTableTab / DocumentsTab). */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

function money(symbol: unknown, value: unknown): string {
  if (value == null) return '—'
  const n = Number(value)
  const digits = Number.isInteger(n) ? 0 : 2
  return `${(symbol as string) || ''}${n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`
}

export default function AccountDealsRoundsTab({
  accountId,
  accountName = null,
  onRefreshReady = null
}: AccountDealsRoundsTabProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const dispatch = useDispatch()
  const quickAdd = useQuickAdd()
  const [data, setData] = useState<AccountDealsRoundsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [addRoundOpen, setAddRoundOpen] = useState(false)
  const [addSecurityOpen, setAddSecurityOpen] = useState(false)
  const [deletingRoundId, setDeletingRoundId] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    if (!accountId) return
    const token = getAccessToken(store.getState().auth)
    const result = await fetchAccountDealsRounds(token, accountId)
    setData(result.data)
    setLoading(false)
  }, [accountId])

  // Deleting a round is refused while a cap table version or a legal agreement
  // set references it; the backend's sentence is what the user sees.
  const handleDeleteRound = useCallback(
    async (roundId: string): Promise<void> => {
      if (!roundId) return
      setDeletingRoundId(roundId)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await deleteRound(token, roundId)
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete round')
          return
        }
        toast.success('Round deleted')
        dispatch(removeRound({ roundId }))
        await refetch()
      } finally {
        setDeletingRoundId(null)
      }
    },
    [dispatch, refetch]
  )

  // Deals are created in other tabs (global add, deal forms) while this tab
  // stays mounted — refresh when the user comes back to it, and let the
  // company form's Refresh button drive it too (owner report 2026-08-26:
  // a new deal never appeared here until a hard refresh).
  const activateRef = useRefreshOnActivate(refetch)
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  // The rounds full form reads from the rounds slice only (no cold by-id fetch),
  // so seed the slice with the row's rounds__* fields before opening the tab —
  // same pattern as RoundOverviewSection's handleRoundHyperlinkClick.
  const openRound = (row: Record<string, unknown>): void => {
    const roundId = row.rounds__id as string
    if (!roundId) return
    // Deals-table rows only carry id+name; the rounds table in the same payload has the full economics
    const fullRow = data?.rounds.find((r) => r.rounds__id === roundId) || row
    const round: Record<string, unknown> = { rounds__account: accountId }
    Object.keys(fullRow).forEach((key) => {
      if (key.startsWith('rounds__')) round[key] = (fullRow as Record<string, unknown>)[key]
    })
    dispatch(addRound({ round: round as any }))
    openFormInTab('rounds', roundId, (round.rounds__name as string) || 'Round')
  }

  const recordLink = (label: string, onClick: () => void): React.ReactElement => (
    <button
      type='button'
      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
      onClick={onClick}
    >
      {label}
    </button>
  )

  useEffect(() => {
    void refetch()
  }, [refetch])

  // The global quick-add creates deals in a drawer OVER this tab (no
  // visibility transition for the activation hook to see), but it does
  // dispatch into the deals slice — watch this company's deal count there
  // and refetch when it moves.
  const companyDealCount = useSelector(
    (state: any) =>
      ((state.deals?.deal?.data || []) as Array<Record<string, unknown>>).filter(
        (d) => d[DealFields.deals__account] === accountId
      ).length
  )
  const dealCountSeenRef = useRef<number | null>(null)
  useEffect(() => {
    if (dealCountSeenRef.current !== null && dealCountSeenRef.current !== companyDealCount) {
      void refetch()
    }
    dealCountSeenRef.current = companyDealCount
  }, [companyDealCount, refetch])

  if (loading || !data) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  return (
    <div ref={activateRef} className='px-2 py-4 space-y-4'>
      <FormSection
        title='Funding Rounds'
        titleColor='blue'
        headerActions={
          <>
            <ExportRowsButton
              name={`${accountName || 'Company'} Funding Rounds`}
              rows={data.rounds}
              columns={[
                { title: 'Round', value: (r) => r.rounds__name },
                { title: 'Stage', value: (r) => r.rounds__funding_stage__label },
                { title: 'Date', value: (r) => r.rounds__round_close_date, kind: 'date' },
                { title: 'Pre-Money', value: (r) => r.rounds__pre_money_valuation },
                { title: 'Post-Money', value: (r) => r.rounds__post_money_valuation },
                { title: 'Size', value: (r) => r.rounds__raise_amount },
                { title: 'Our Investment', value: (r) => r.prog__our_investment },
                { title: 'Cap Table', value: (r) => (r.prog__cap_table ? 'Yes' : null) },
                // the Our Deals tag as two columns: how many of our deals, and Passed
                { title: 'Our Deals', value: (r) => (Array.isArray(r.prog__deals) && r.prog__deals.length > 0 ? r.prog__deals.length : null) },
                {
                  title: 'Passed',
                  value: (r) => (Array.isArray(r.prog__deals) && r.prog__deals.length > 0 && r.prog__passed ? 'Yes' : null)
                }
              ]}
            />
            <Tooltip title='Refresh'>
              <Button size='small' type='text' icon={<ReloadOutlined />} onClick={() => refetch()} />
            </Tooltip>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              disabled={!accountId}
              onClick={() => setAddRoundOpen(true)}
            >
              Round
            </Button>
          </>
        }
      >
        <Table
          size='small'
          rowKey='rounds__id'
          pagination={false}
          dataSource={data.rounds}
          columns={[
            {
              title: 'Round',
              dataIndex: 'rounds__name',
              render: (v, row) =>
                row.rounds__id
                  ? recordLink((v as string) || 'Unnamed round', () => openRound(row))
                  : v || '—'
            },
            { title: 'Stage', dataIndex: 'rounds__funding_stage__label', render: (v) => v || '—' },
            {
              title: 'Date',
              dataIndex: 'rounds__round_close_date',
              render: (v) => (v ? dayjs(String(v)).format('MM/DD/YYYY') : '—')
            },
            {
              title: 'Pre-Money',
              dataIndex: 'rounds__pre_money_valuation',
              align: 'right' as const,
              render: (v) => money((data.deals[0] || {}).currencies__symbol, v)
            },
            {
              title: 'Post-Money',
              dataIndex: 'rounds__post_money_valuation',
              align: 'right' as const,
              render: (v) => money((data.deals[0] || {}).currencies__symbol, v)
            },
            {
              title: 'Size',
              dataIndex: 'rounds__raise_amount',
              align: 'right' as const,
              render: (v) => money((data.deals[0] || {}).currencies__symbol, v)
            },
            {
              title: 'Our Investment',
              dataIndex: 'prog__our_investment',
              align: 'right' as const,
              render: (v) => money((data.deals[0] || {}).currencies__symbol, v)
            },
            {
              title: 'Cap Table',
              dataIndex: 'prog__cap_table',
              align: 'center' as const,
              render: (v) =>
                v ? <Tag color='green'>Yes</Tag> : <span className='text-gray-300'>—</span>
            },
            {
              title: 'Our Deals',
              key: 'deals',
              render: (_, row) => {
                const deals = (row.prog__deals as Array<{ id: string; name: string }>) || []
                if (deals.length === 0) return <span className='text-gray-300'>—</span>
                if (row.prog__passed) return <Tag color='red'>Passed</Tag>
                return (
                  <Tag color='blue'>
                    {deals.length} deal{deals.length === 1 ? '' : 's'}
                  </Tag>
                )
              }
            },
            {
              // Icon-only destructive row action keeps type='text' (house
              // pattern). The backend refuses while a cap table version or a
              // legal set references the round, and says why.
              title: '',
              key: 'actions',
              width: 44,
              align: 'center' as const,
              render: (_, row) => (
                <Popconfirm
                  title='Delete this round?'
                  description='Its securities and co-investors go with it, and any linked deals are unlinked.'
                  okText='Delete'
                  cancelText='Cancel'
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDeleteRound(row.rounds__id as string)}
                >
                  <Tooltip title='Delete round'>
                    <Button
                      size='small'
                      type='text'
                      icon={<DeleteOutlined />}
                      loading={deletingRoundId === row.rounds__id}
                    />
                  </Tooltip>
                </Popconfirm>
              )
            }
          ]}
        />
      </FormSection>

      <FormSection
        title='Deals'
        titleColor='green'
        headerActions={
          <>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              disabled={!accountId || !quickAdd}
              onClick={() =>
                quickAdd?.openQuickAdd('deal', {
                  // {value,label} — the drawer's company field holds the pair
                  // (extractCompanyId unwraps it at save)
                  [DealFields.deals__account]: {
                    value: accountId,
                    label: accountName || 'Company'
                  }
                })
              }
            >
              Deal
            </Button>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              disabled={!accountId}
              onClick={() => setAddSecurityOpen(true)}
            >
              Security
            </Button>
          </>
        }
      >
        <Table
          size='small'
          rowKey='deals__id'
          pagination={false}
          dataSource={data.deals}
          columns={[
            {
              title: 'Deal',
              dataIndex: 'deals__name',
              render: (v, row) =>
                recordLink((v as string) || 'Unnamed deal', () =>
                  openFormInTab('deals', row.deals__id as string, (v as string) || 'Deal')
                )
            },
            {
              title: 'Type',
              dataIndex: 'deals__deal_type__label',
              render: (v) => (v ? <span className='text-xs text-gray-500'>{String(v)}</span> : '—')
            },
            {
              title: 'Origination',
              key: 'orig',
              render: (_, row) =>
                row.prog__orig_stage ? (
                  // Stage name only — decision_status is an internal value users never see
                  <Tag>{String(row.prog__orig_stage)}</Tag>
                ) : (
                  <span className='text-gray-300'>—</span>
                )
            },
            {
              title: 'Execution',
              key: 'exec',
              render: (_, row) => {
                if (!row.prog__exec_stage) return <span className='text-gray-300'>—</span>
                const days = daysSince((row.prog__exec_stage_date as string) || null)
                return (
                  <Tag color='gold'>
                    {String(row.prog__exec_stage)}
                    {days != null ? ` · ${days}d` : ''}
                  </Tag>
                )
              }
            },
            { title: 'Funds', dataIndex: 'prog__funds', render: (v) => v || '—' },
            {
              title: 'Commitment',
              dataIndex: 'prog__commitment_total',
              align: 'right' as const,
              render: (v, row) => money(row.currencies__symbol, v)
            },
            {
              title: 'Round',
              dataIndex: 'rounds__name',
              render: (v, row) =>
                row.rounds__id
                  ? recordLink((v as string) || 'Unnamed round', () => openRound(row))
                  : <span className='text-gray-300'>no round</span>
            }
          ]}
        />
      </FormSection>

      <AddRoundModal
        open={addRoundOpen}
        companyId={accountId}
        companyName={accountName}
        onCancel={() => setAddRoundOpen(false)}
        onCreated={async () => {
          setAddRoundOpen(false)
          await refetch()
        }}
      />
      {/* Securities have no list on this tab (they live on rounds and
          transactions) — the button is the company-scoped create. */}
      <AddSecurityModal
        open={addSecurityOpen}
        companyId={accountId}
        companyName={accountName}
        onCancel={() => setAddSecurityOpen(false)}
        onCreated={(securityId, securityName) => {
          setAddSecurityOpen(false)
          openFormInTab('securities', securityId, securityName || 'Security')
        }}
      />
    </div>
  )
}
